import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Link } from 'react-router-dom';

const quotes = [
  {
    number: '75',
    suffix: '%',
    thesis: 'der B2B-Käufer bevorzugen ein vertriebsfreies Kauferlebnis.',
    copy: 'Ihre Website ist längst nicht mehr nur digitale Visitenkarte. Sie ist oft der erste echte Vertriebskanal und entscheidet mit darüber, ob Vertrauen entsteht oder nicht.',
  },
  {
    number: '53',
    suffix: '%',
    thesis: 'der mobilen Besucher springen ab, wenn eine Seite länger als 3 Sekunden lädt.',
    copy: 'Eine veraltete, langsame Website kostet nicht nur Aufmerksamkeit, sondern konkrete Anfragen, Leads und potenziellen Umsatz.',
  },
];

// Scroll budget:
// headline: 0.5
// quote 0 num: 0.5, thesis: 0.5, copy: 0.5
// quote 1 num: 0.5, thesis: 0.5, copy: 0.5
// CTA: 0.5
// Total: 4.0
const STEP = 0.25;
const TOTAL = STEP * 8; // 2.0

function easeOut(t: number, power = 2.5): number {
  return 1 - Math.pow(1 - Math.max(0, Math.min(1, t)), power);
}

export default function Statistics() {
  const { theme } = useTheme();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const sectionHeight = section.offsetHeight - window.innerHeight;
      if (sectionHeight <= 0) return;
      const scrolled = -rect.top;
      const p = Math.max(0, Math.min(TOTAL, (scrolled / sectionHeight) * TOTAL));
      setProgress(p);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const layerIn = (start: number) => easeOut((progress - start) / STEP, 2.5);

  // Headline – bereits sichtbar wenn Sektion pinnt
  const headlineP = layerIn(-STEP);

  // Quote 0
  const q0_num   = layerIn(STEP * 0.5);
  const q0_thesis = layerIn(STEP * 2);
  const q0_copy   = layerIn(STEP * 3);

  // Quote 1
  const q1_num   = layerIn(STEP * 4);
  const q1_thesis = layerIn(STEP * 5);
  const q1_copy   = layerIn(STEP * 6);

  // CTA
  const ctaP = easeOut((progress - STEP * 7) / STEP, 2);

  // Scroll hint
  const scrollHintOpacity = Math.max(0, 1 - progress * 4);

  // Number counters
  const [displayNum0, setDisplayNum0] = useState(0);
  const [displayNum1, setDisplayNum1] = useState(0);
  useEffect(() => {
    setDisplayNum0(Math.round(q0_num * parseInt(quotes[0].number)));
    setDisplayNum1(Math.round(q1_num * parseInt(quotes[1].number)));
  }, [q0_num, q1_num]);

  const totalHeight = `${(TOTAL + 1) * 100}vh`;

  const layerStyle = (
    p: number,
    dir: 'left' | 'right' | 'up' = 'left',
    distance = 60
  ): React.CSSProperties => {
    const tx = dir === 'left' ? -(1 - p) * distance : dir === 'right' ? (1 - p) * distance : 0;
    const ty = dir === 'up' ? (1 - p) * distance : 0;
    return {
      opacity: p,
      transform: `translate(${tx}px, ${ty}px)`,
      transition: 'none',
      willChange: 'transform, opacity',
    };
  };

  return (
    <div
      ref={sectionRef}
      style={{ height: totalHeight, fontFamily: theme.fonts.body }}
      className="relative"
    >
      <div
        className="sticky top-0 h-screen flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: '#0A0A0A' }}
      >
        {/* Ambient glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse 80% 70% at 50% 50%, ${theme.colors.accent}15 0%, transparent 65%)`,
          }}
        />

        {/* Fine grid overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(${theme.colors.accent} 1px, transparent 1px), linear-gradient(90deg, ${theme.colors.accent} 1px, transparent 1px)`,
            backgroundSize: '80px 80px',
          }}
        />

        {/* Top label */}
        <div className="absolute top-8 sm:top-10 left-0 right-0 flex items-center justify-between px-4 sm:px-8 md:px-12 z-20">
          <span
            className="text-xs tracking-[0.3em] uppercase font-medium"
            style={{ color: `${theme.colors.accent}80` }}
          >
            Fakten &amp; Zahlen
          </span>
        </div>

        {/* ─── Main content ─── */}
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-8 md:px-20 flex flex-col gap-10 sm:gap-16 lg:gap-20">

          {/* Headline */}
          <div style={layerStyle(headlineP, 'up', 50)}>
            <h2
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black leading-tight text-left sm:text-center"
              style={{ fontFamily: theme.fonts.heading, color: '#FFFFFF' }}
            >
              Was die Zahlen sagen.
            </h2>
          </div>

          {/* Quotes */}
          <div className="flex flex-col gap-8 sm:gap-12 lg:gap-14">
            {/* Quote 0 */}
            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5 sm:gap-8 md:gap-14 items-start">
              {/* Number */}
              <div style={layerStyle(q0_num, 'left', 80)}>
                <div
                  className="font-black leading-none select-none whitespace-nowrap"
                  style={{
                    fontFamily: theme.fonts.heading,
                    fontSize: 'clamp(48px, 10vw, 120px)',
                    background: `linear-gradient(135deg, #FFFFFF 0%, ${theme.colors.accent} 60%, ${theme.colors.secondary} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    lineHeight: 0.9,
                  }}
                >
                  {displayNum0}
                  <span style={{ fontSize: '0.5em' }}>{quotes[0].suffix}</span>
                </div>
                <div
                  className="mt-3 h-px"
                  style={{
                    width: `${q0_num * 100}%`,
                    maxWidth: '80px',
                    background: `linear-gradient(90deg, ${theme.colors.accent}, transparent)`,
                    transition: 'none',
                  }}
                />
              </div>
              {/* Text */}
              <div className="flex flex-col gap-3 pt-2">
                <h3
                  className="text-xl md:text-2xl font-bold leading-snug"
                  style={{
                    fontFamily: theme.fonts.heading,
                    color: '#FFFFFF',
                    ...layerStyle(q0_thesis, 'right', 50),
                  }}
                >
                  {quotes[0].thesis}
                </h3>
                <p
                  className="text-sm md:text-base leading-relaxed"
                  style={{
                    color: 'rgba(255,255,255,0.45)',
                    ...layerStyle(q0_copy, 'left', 35),
                  }}
                >
                  {quotes[0].copy}
                </p>
              </div>
            </div>

            {/* Divider */}
            <div
              className="h-px w-full"
              style={{
                background: `linear-gradient(90deg, transparent, ${theme.colors.accent}30, transparent)`,
                opacity: Math.min(q0_copy, 1),
              }}
            />

            {/* Quote 1 */}
            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5 sm:gap-8 md:gap-14 items-start">
              {/* Number */}
              <div style={layerStyle(q1_num, 'left', 80)}>
                <div
                  className="font-black leading-none select-none whitespace-nowrap"
                  style={{
                    fontFamily: theme.fonts.heading,
                    fontSize: 'clamp(48px, 10vw, 120px)',
                    background: `linear-gradient(135deg, #FFFFFF 0%, ${theme.colors.accent} 60%, ${theme.colors.secondary} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    lineHeight: 0.9,
                  }}
                >
                  {displayNum1}
                  <span style={{ fontSize: '0.5em' }}>{quotes[1].suffix}</span>
                </div>
                <div
                  className="mt-3 h-px"
                  style={{
                    width: `${q1_num * 100}%`,
                    maxWidth: '80px',
                    background: `linear-gradient(90deg, ${theme.colors.accent}, transparent)`,
                    transition: 'none',
                  }}
                />
              </div>
              {/* Text */}
              <div className="flex flex-col gap-3 pt-2">
                <h3
                  className="text-xl md:text-2xl font-bold leading-snug"
                  style={{
                    fontFamily: theme.fonts.heading,
                    color: '#FFFFFF',
                    ...layerStyle(q1_thesis, 'right', 50),
                  }}
                >
                  {quotes[1].thesis}
                </h3>
                <p
                  className="text-sm md:text-base leading-relaxed"
                  style={{
                    color: 'rgba(255,255,255,0.45)',
                    ...layerStyle(q1_copy, 'left', 35),
                  }}
                >
                  {quotes[1].copy}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ─── CTA ─── */}
        <div
          className="absolute bottom-14 sm:bottom-20 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4"
          style={{
            opacity: ctaP,
            transform: `translateX(-50%) translateY(${(1 - ctaP) * 20}px)`,
            pointerEvents: ctaP > 0.5 ? 'auto' : 'none',
            transition: 'none',
          }}
        >
          <Link to="/website-wirkung">
            <button
              className="group relative px-10 py-4 font-bold text-sm tracking-widest uppercase rounded-full overflow-hidden whitespace-nowrap cursor-pointer"
              style={{
                fontFamily: theme.fonts.heading,
                border: `1.5px solid ${theme.colors.accent}60`,
                color: '#FFFFFF',
                background: 'transparent',
              }}
            >
              <span
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"
                style={{ background: `linear-gradient(135deg, ${theme.colors.secondary}CC, ${theme.colors.accent}CC)` }}
              />
              <span className="relative z-10">Mehr Fakten</span>
            </button>
          </Link>
        </div>

        {/* Scroll hint */}
        <div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          style={{ opacity: scrollHintOpacity, transition: 'none' }}
        >
          <span
            className="text-xs tracking-[0.25em] uppercase"
            style={{ color: `${theme.colors.accent}60` }}
          >
            Scrollen
          </span>
          <div className="flex flex-col items-center gap-1">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-px rounded-full"
                style={{
                  height: '6px',
                  backgroundColor: theme.colors.accent,
                  opacity: 0.3 + i * 0.25,
                  animation: `bounce-dot 1.4s ${i * 0.15}s infinite`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Bottom progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ backgroundColor: `${theme.colors.accent}15` }}>
          <div
            className="h-full"
            style={{
              width: `${(progress / TOTAL) * 100}%`,
              background: `linear-gradient(90deg, ${theme.colors.secondary}, ${theme.colors.accent})`,
              transition: 'none',
              boxShadow: `0 0 8px ${theme.colors.accent}80`,
            }}
          />
        </div>

        <style>{`
          @keyframes bounce-dot {
            0%, 100% { transform: translateY(0); opacity: 0.3; }
            50% { transform: translateY(-4px); opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  );
}
